/**
 * reuse-strategy.ts
 * by corbfon 1/6/17
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouteReuseStrategy, DetachedRouteHandle } from '@angular/router';
import { Subject } from 'rxjs';
import { DBG, DebugLevelEnum } from './dbg.model';
import { IReuseStrategy } from './ireusestrategy.model';

/** Interface for object which can store both: 
 * An ActivatedRouteSnapshot, which is useful for determining whether or not you should attach a route (see this.shouldAttach)
 * A DetachedRouteHandle, which is offered up by this.retrieve, in the case that you do want to attach the stored route
 */
interface RouteStorageObject {
    snapshot: ActivatedRouteSnapshot;
    handle: DetachedRouteHandle;
    date: Date;
    type: string;
    id: string;
}

interface IAppReuseStrategyEvent {
    type: string;
    value: any;
}


@Injectable()
export class AppReuseStrategy implements RouteReuseStrategy {

    TAG: string = "AppReuseStrategy";
    cacheEvent = new Subject<IAppReuseStrategyEvent>();
    /** 
     * Object which will store RouteStorageObjects indexed by keys
     * The keys will all be a path (as in route.routeConfig.path)
     * This allows us to see if we've got a route stored for the requested path
     */
    //storedRoutes: { [key: string]: RouteStorageObject } = {};
    //private _storedRoutes: { key: string; route: RouteStorageObject; }[] = [];
    private _storedRoutes = new Map<string, RouteStorageObject>();
    storedRouteGetByKey(key: string): RouteStorageObject {
        //return this._storedRoutes.find(item=>item.key==key);
        return this._storedRoutes.get(key);
    }
    storedRouteAdd(key: string, route: RouteStorageObject) {
        this._storedRoutes.set(key, route);
    }

    storedRoutesClear() {
        this._storedRoutes.clear();
    }


    public acceptedRoutes: string[] = ["/solar/dashboard", "/users/users-list", "/solar/sites", "/solar/supervision-list", "/solar/supervision-master", "/solar/supervisioncomparer"];

    /**
     * when set the route cache is cleared
     */
    public routesForClearReset: string[] = ["/login"];

    /** 
     * Decides when the route should be stored
     * If the route should be stored, I believe the boolean is indicating to a controller whether or not to fire this.store
     * _When_ it is called though does not particularly matter, just know that this determines whether or not we store the route
     * An idea of what to do here: check the route.routeConfig.path to see if it is a path you would like to store
     * @param route This is, at least as I understand it, the route that the user is currently on, and we would like to know if we want to store it
     * @returns boolean indicating that we want to (true) or do not want to (false) store that route
     */
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        /*       let detach: boolean = true;
              DBG.log(DebugLevelEnum.VERBOSE,this.TAG,"detaching", route, "return: ", detach);
              return detach; */

        const routeConfigPath = this.getPath(route);
        DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "shouldDetach() START--------------------------" + routeConfigPath);
        // check to see if the route's path is in our acceptedRoutes array
        for (let acceptedRoute of this.acceptedRoutes) {
            if (acceptedRoute == routeConfigPath) {
                if (routeConfigPath == "/solar/supervision-master") {
                    //todo:limitar el numero de supervisiones
                    this.removeLastOfType("SUPERVISION", 2);
                }
                DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "shouldDetach() detaching", route);
                return true;
            } else {

            }
        }

        return false; // will be "view/:resultId" when user navigates to result

    }

    /**
     * method to remove the last component of some tipe
     * @param type 
     * @param max 
     */
    removeLastOfType(type: string, max: number) {

        let lst = [...this._storedRoutes.entries()].filter(item => item[1].type == type);//same as Array.from(map.values()).filter((item: Event) => item.event_id === eventId);
        if (lst.length > max) {
            lst = lst.sort((a, b) => b[1].date.valueOf() - a[1].date.valueOf());
            let last = lst[lst.length - 1][0];
            this._storedRoutes.delete(last);
        }
        //DBG.log(DebugLevelEnum.VERBOSE,this.TAG,"this._storedRoutes.length: " + this._storedRoutes.length);

    }

    private getPath(route: ActivatedRouteSnapshot): string {
        if (route.routeConfig !== null && route.routeConfig.path !== null) {
            return this.getResolvedUrl(route);
        }
        return '';
    }

    private getResolvedUrl(route: ActivatedRouteSnapshot): string {
        let path = "";
        for (let segment of route.pathFromRoot) {
            if (segment.url && segment.url.length > 0 && segment.url[0]) {
                path += "/" + segment.url[0].path;
            }
        }
        return path;
    }


    /**
     * Constructs object of type `RouteStorageObject` to store, and then stores it for later attachment
     * @param route This is stored for later comparison to requested routes, see `this.shouldAttach`
     * @param handle Later to be retrieved by this.retrieve, and offered up to whatever controller is using this class
     */
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {


        if (handle == null) {
            //angular route send a null object when is attaching. Now it doesn´t interfere with our code
            return;
        }

        let storedRoute: RouteStorageObject = {
            snapshot: route,
            handle: handle,
            date: new Date(),
            type: "ROUTE",
            id: ""
        };

        DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "store:", storedRoute);
        let routeConfigPath = this.getPath(route);
        if (routeConfigPath == "/solar/supervision-master") {
            storedRoute.type = "SUPERVISION";
            routeConfigPath = this.getSpecificRoute(route);
            //todo:test remove tooltip
            
            
        }
        let obj: any = (storedRoute.handle);
        this.hideToolTips(obj.componentRef.instance);

        this.cacheEventRaise("STORE", routeConfigPath, storedRoute);
        //this.checkDetachOnComponent(storedRoute);

        // routes are stored by path - the key is the path name, and the handle is stored under it so that you can only ever have one object stored for a single path
        let cached = this.storedRouteGetByKey(routeConfigPath);
        if (!cached) {
            this.storedRouteAdd(routeConfigPath, storedRoute);
        }

    }


    hideToolTips(obj: any) {
        return;
        //no funciona, sigue sin ocultar el tooltip
        DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "hideToolTips:", obj);

        if (!obj.hasOwnProperty("matTooltipChildren")){
            return;
        }
        let matTooltipChildren: any = obj.matTooltipChildren;
        //matTooltipChildren.forEach(elem => elem.hide());
        let res=matTooltipChildren._results;
        for (let x=0; x<res.length;x++){
            let tt=res[x];
            tt.hide(0);
            tt._visibility = 'hidden';
            tt._hideTimeoutId = undefined;

      // Mark for check so if any parent component has set the
      // ChangeDetectionStrategy to OnPush it will be checked anyways
            tt._changeDetectorRef.markForCheck();
            console.log(tt);
        }
    }

    cacheEventRaise(eventType: string, routeConfigPath: string, storedRoute: RouteStorageObject) {

        if (storedRoute.handle == null) {
            return;
        }
        let componentRef = (storedRoute.handle as any).componentRef;
        if (componentRef == null) {
            return;
        }
        this.cacheEvent.next({ type: eventType, value: routeConfigPath });
    }


    private checkDetachOnComponent(storedRoute: RouteStorageObject) {
        if (storedRoute.handle == null) {
            return;
        }
        let componentRef = (storedRoute.handle as any).componentRef;
        if (componentRef == null) {
            return;
        }
        let hasIRouteInterface = 'reuseStrategy_Detach' in componentRef.instance;

        //DBG.log(DebugLevelEnum.VERBOSE,this.TAG,"hasIRouteInterface reuseStrategy_Detach: " + hasIRouteInterface);
        if (hasIRouteInterface) {
            let obj = (storedRoute.handle as any).componentRef.instance as IReuseStrategy;
            //obj.reuseStrategy_Detach();
        }
    }

    private checkAtachOnComponent(storedRoute: RouteStorageObject) {
        if (storedRoute.handle == null) {
            return;
        }
        let componentRef = (storedRoute.handle as any).componentRef;
        if (componentRef == null) {
            return;
        }
        let hasIRouteInterface = 'reuseStrategy_Atach' in componentRef.instance;
        DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "hasIRouteInterface reuseStrategy_Atach: " + hasIRouteInterface);
        if (hasIRouteInterface) {
            let obj = (storedRoute.handle as any).componentRef.instance as IReuseStrategy;
            //obj.reuseStrategy_Atach();
        }
    }


    getSpecificRoute(route: ActivatedRouteSnapshot): string {
        let routeConfigPath = this.getPath(route);
        if (routeConfigPath == "/solar/supervision-master" && route.params.id !== "") {
            routeConfigPath += "/" + route.params.id;
        }

        return routeConfigPath;
    }

    /**
     * Determines whether or not there is a stored route and, if there is, whether or not it should be rendered in place of requested route
     * @param route The route the user requested
     * @returns boolean indicating whether or not to render the stored route
     */
    shouldAttach(route: ActivatedRouteSnapshot): boolean {

        // this will be true if the route has been stored before
        let routeConfigPath = this.getSpecificRoute(route);
        // check to see if the route's path is in our acceptedRoutes array
        for (let clearRoute of this.routesForClearReset) {
            if (clearRoute == routeConfigPath) {
                this.storedRoutesClear();
                return false;
            }
        }

        let storedComponentItem = this.storedRouteGetByKey(routeConfigPath);
        let canAttach: boolean = !!route.routeConfig && !!storedComponentItem;


        // this decides whether the route already stored should be rendered in place of the requested route, and is the return value
        // at this point we already know that the paths match because the storedResults key is the route.routeConfig.path
        // so, if the route.params and route.queryParams also match, then we should reuse the component
        if (canAttach) {
            let storedComponent = storedComponentItem;
            DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "shouldAttach()------------------------------------");
            let willAttach: boolean = true;
            let compareObjects = this.compareObjects(route.params, storedComponent.snapshot.params);
            let compareParams = this.compareObjects(route.queryParams, storedComponent.snapshot.queryParams);
            DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "param comparison: " + compareObjects + " query param comparison: " + compareParams);


            let paramsMatch: boolean = this.compareObjects(route.params, storedComponent.snapshot.params);
            let queryParamsMatch: boolean = this.compareObjects(route.queryParams, storedComponent.snapshot.queryParams);

            DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "deciding to attach...", route, "does it match?", storedComponent.snapshot, "return: ", paramsMatch && queryParamsMatch);
            let canAtach = paramsMatch && queryParamsMatch;
            if (canAtach) {
                //IMPORTANT:this event must be called here because in angular route code they call retrieve and store null at the same time so this event will be called two times                
                this.cacheEventRaise("RESTORE", routeConfigPath, storedComponent);
            }
            return canAtach;
        } else {
            return false;
        }
    }

    /** 
     * Finds the locally stored instance of the requested route, if it exists, and returns it
     * @param route New route the user has requested
     * @returns DetachedRouteHandle object which can be used to render the component
     */
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
        const routeConfigPath = this.getSpecificRoute(route);
        let storedComponent: RouteStorageObject = this.storedRouteGetByKey(routeConfigPath);

        // return null if the path does not have a routerConfig OR if there is no stored route for that routerConfig
        if (!route.routeConfig || !storedComponent) {
            //check handle and component ref??? like in cacheEventRaise method
            return null;
        }

        DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "retrieving", "return: ", storedComponent);

        /** returns handle when the route.routeConfig.path is already stored */

        return storedComponent.handle;
    }

    /** 
     * Determines whether or not the current route should be reused
     * @param future The route the user is going to, as triggered by the router
     * @param curr The route the user is currently on
     * @returns boolean basically indicating true if the user intends to leave the current route
     */
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        DBG.log(DebugLevelEnum.VERBOSE, this.TAG, "shouldReuseRoute() deciding to reuse", "future", future.routeConfig, "current", curr.routeConfig, "return: ", future.routeConfig === curr.routeConfig);
        //this.cacheEvent.next({future:future,curr:curr});
        return future.routeConfig === curr.routeConfig;
    }

    /** 
     * This nasty bugger finds out whether the objects are _traditionally_ equal to each other, like you might assume someone else would have put this function in vanilla JS already
     * One thing to note is that it uses coercive comparison (==) on properties which both objects have, not strict comparison (===)
     * Another important note is that the method only tells you if `compare` has all equal parameters to `base`, not the other way around
     * @param base The base object which you would like to compare another object to
     * @param compare The object to compare to base
     * @returns boolean indicating whether or not the objects have all the same properties and those properties are ==
     */
    private compareObjects(base: any, compare: any): boolean {

        // loop through all properties in base object
        for (let baseProperty in base) {

            // determine if comparrison object has that property, if not: return false
            if (compare.hasOwnProperty(baseProperty)) {
                switch (typeof base[baseProperty]) {
                    // if one is object and other is not: return false
                    // if they are both objects, recursively call this comparison function
                    case 'object':
                        if (typeof compare[baseProperty] !== 'object' || !this.compareObjects(base[baseProperty], compare[baseProperty])) { return false; } break;
                    // if one is function and other is not: return false
                    // if both are functions, compare function.toString() results
                    case 'function':
                        if (typeof compare[baseProperty] !== 'function' || base[baseProperty].toString() !== compare[baseProperty].toString()) { return false; } break;
                    // otherwise, see if they are equal using coercive comparison
                    default:
                        if (base[baseProperty] != compare[baseProperty]) { return false; }
                }
            } else {
                return false;
            }
        }

        // returns true only after false HAS NOT BEEN returned through all loops
        return true;
    }
}